import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { fetchDelegations } from '@/api'
import CopyWithTooltip from '@/common/CopyWithTooltip'
import ResponsiveModalDrawer from '@/common/ResponsiveModalDrawer'
import ValidatorSelect from '@/common/ValidatorSelect'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import {
  emitter,
  ErrorHandler,
  formatAddress,
  formatBridgeAtomic,
  isPositiveDecimalAmount,
} from '@/helpers'
import { buildRedelegateNftMsg, RedelegateError } from '@/libs/keplr/messages'
import { invalidateNftScope } from '@/queries/invalidate'
import { queryKeys } from '@/queries/keys'
import UiRing from '@/ui/loaders/UiRing'
import { UiButton } from '@/ui/UiButton'

type Props = {
  open: boolean
  nftAddress: string
  onClose: () => void
}

export default function RedelegateModal({ open, nftAddress, onClose }: Props) {
  const { address, signAndBroadcast } = useKeplrWallet()
  const queryClient = useQueryClient()

  // Fetch current delegation to get source validator and delegated amount
  const { data: delegationData, isLoading: isLoadingDelegation } = useQuery({
    queryKey: queryKeys.staking.delegations(nftAddress),
    enabled: Boolean(open && nftAddress),
    queryFn: async () => {
      return fetchDelegations(nftAddress)
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  const delegation = useMemo(() => {
    const delegationResponse = delegationData?.delegation_responses?.[0]
    const shares = delegationResponse?.delegation?.shares
    const validator = delegationResponse?.delegation?.validator_address ?? ''

    if (!shares || !validator) return null

    return {
      validator,
      shares,
      balanceAtomic: delegationResponse?.balance?.amount,
    }
  }, [delegationData])

  const validatorSrc = delegation?.validator ?? ''
  const hasDelegation = isPositiveDecimalAmount(delegation?.shares)

  const [validatorNew, setValidatorNew] = useState('')
  const isSameValidator = Boolean(
    validatorSrc && validatorNew && validatorSrc === validatorNew,
  )

  const redelegateTx = useMutation({
    mutationKey: queryKeys.tx.redelegate(nftAddress),
    mutationFn: async () => {
      if (!address) throw new Error('Wallet not connected')
      if (!delegation || !hasDelegation) {
        throw new Error('No delegation found')
      }
      if (!validatorNew) throw new Error('New validator is required')
      if (isSameValidator) {
        throw new Error('Cannot redelegate to the same validator')
      }

      const msg = buildRedelegateNftMsg({
        creator: address,
        address: nftAddress,
        validatorSrc,
        validatorNew,
        share: delegation.shares,
      })

      return signAndBroadcast([msg])
    },

    onSuccess: txHash => {
      emitter.emit('success', {
        title: 'Redelegation sent',
        message: `TxHash: ${formatAddress(txHash)}`,
      })
      invalidateNftScope(queryClient, nftAddress)
      onClose()
    },
    onError: error => {
      ErrorHandler.process(RedelegateError.fromUnknown(error))
    },
  })

  const isSubmitting = redelegateTx.isPending

  return (
    <ResponsiveModalDrawer
      open={open}
      onOpenChange={v => !v && !isSubmitting && onClose()}
      title={<span className='text-foreground'>Redelegate NFT</span>}
      content={
        <div className='text-foreground mt-4 flex flex-col gap-4 p-1'>
          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>NFT Address</span>
            <div className='bg-input flex h-12 items-center gap-2 rounded-lg px-3 py-2'>
              <span className='muted-foreground text-sm'>
                {formatAddress(nftAddress, 20, 15)}
              </span>
              <div className='ml-auto'>
                <CopyWithTooltip value={nftAddress} />
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>
              From Validator
            </span>
            <div className='bg-input flex h-12 items-center gap-2 rounded-lg px-3 py-2'>
              <span className='muted-foreground text-sm'>
                {validatorSrc ? formatAddress(validatorSrc, 20, 15) : '…'}
              </span>
              {validatorSrc && (
                <div className='ml-auto'>
                  <CopyWithTooltip value={validatorSrc} />
                </div>
              )}
            </div>
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>To Validator</span>
            <ValidatorSelect
              value={validatorNew}
              disabled={isSubmitting || isLoadingDelegation || !hasDelegation}
              triggerClassName='w-full'
              contentClassName='w-full'
              onChange={v => setValidatorNew(v)}
            />
            {isSameValidator && (
              <span className='text-xs text-red-400'>
                Cannot redelegate to the same validator
              </span>
            )}
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>Amount</span>
            <div className='bg-input text-foreground flex min-h-12 items-center rounded-lg px-3 py-2 text-sm break-all'>
              {isLoadingDelegation
                ? '…'
                : delegation
                  ? `${formatBridgeAtomic(delegation.balanceAtomic ?? '0')} BRIDGE`
                  : 'No active delegation found'}
            </div>
            {delegation && !hasDelegation && (
              <span className='text-xs text-red-400'>
                No delegated shares found for this NFT
              </span>
            )}
            {!delegation && (
              <span className='text-xs text-red-400'>
                No active delegation found for this NFT
              </span>
            )}
          </div>
        </div>
      }
      footer={
        <div className='flex w-full gap-2'>
          <UiButton
            variant='outline2'
            className='h-12 flex-1'
            onClick={onClose}
            disabled={isSubmitting}
          >
            Close
          </UiButton>

          <UiButton
            variant='outline'
            className='h-12 flex-1'
            disabled={
              isSubmitting ||
              isLoadingDelegation ||
              !hasDelegation ||
              !validatorSrc ||
              !validatorNew ||
              isSameValidator
            }
            onClick={() => redelegateTx.mutate()}
          >
            {isSubmitting ? (
              <span className='flex items-center gap-2'>
                <UiRing size='15' stroke='2' speed='1.65' color='white' />
                Redelegating…
              </span>
            ) : (
              'Redelegate'
            )}
          </UiButton>
        </div>
      }
      modalProps={{ className: 'bg-card text-foreground rounded-lg max-w-md' }}
      drawerProps={{ className: 'bg-card text-foreground rounded-lg' }}
    />
  )
}
